/*
 * footer-signup.js: the footer email sign-up (sections/footer.liquid, form#ContactFooter). su202, ref zao#201.
 *
 * Why this file exists
 * - The footer used to post Shopify's customer form. Those sign-ups waited on Shopify's own email confirmation and
 *   most never reached Klaviyo, so nobody who signed up in the footer got the sign-up offer.
 * - This sends the address to Klaviyo's public client subscription API instead, straight onto list XdwVYY (the list
 *   the welcome + offer flow listens to; it is double opt-in, so Klaviyo sends the one confirmation email). Nothing
 *   is sent to Shopify any more, so nobody gets two confirmation emails.
 *
 * Request (Klaviyo "Create Client Subscription", revision 2026-07-15). Public company id only, no private key:
 *   POST https://a.klaviyo.com/client/subscriptions?company_id=VMQtQt
 *   { data: { type: 'subscription',
 *             attributes: { custom_source: 'Footer',
 *                           profile: { data: { type: 'profile', attributes: {
 *                             email,
 *                             subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
 *                             properties: { signup_offer: 'gloves' | 'pct10', signup_source: 'footer' } } } } },
 *             relationships: { list: { data: { type: 'list', id: 'XdwVYY' } } } } }
 *   Klaviyo answers 202 Accepted. The thank-you shows only on a 2xx; anything else, a network failure, or 15 s
 *   without an answer shows the "try again" message (the same handling as assets/notify-me.js). After a real 2xx the
 *   address is also given to Klaviyo's own onsite script (klaviyo.identify), so Klaviyo's pop-ups stop asking her.
 *
 * The offer (A/B): 'gloves' or 'pct10', kept in localStorage (sofsy_signup_offer) so she sees the same offer every
 * visit. It follows the Klaviyo pop-up's arm when known, else the footer's own 50/50; see "the offer" below. The
 * form's data-offer attribute shows the matching value line and button words (CSS in sofsy-ds.css).
 * The form stays invisible until this script has set it up (footer-signup--ready), so no wrong offer flashes and a
 * script that fails to load never leaves a button that does nothing.
 *
 * Once she touches the form, its offer is locked for the page view (later pop-up events only update storage).
 *
 * Bot protection (the endpoint has no captcha): a hidden trap field (people never see or reach it) and a minimum of
 * 1 s between her first touch of the form (focus or typing) and the send. A filled trap or a send with no touch at
 * all gets a pretend thank-you; nothing is sent. A send under 1 s with the trap empty is never dropped: the button
 * shows its usual sending state, the send waits out the rest of the 1 s, then goes as normal (a shopper who taps the
 * keyboard's email suggestion can be that quick; dropping her showed "check your inbox" and sent nothing). XdwVYY's
 * double opt-in already keeps a bot that gets through from receiving a code.
 *
 * Tracking (sofsy-track.js): after a real 2xx, and only then, subscribe() hands the sign-up to
 * SofsyTrack.signupSuccess (the one lead decider: email.signup.success / drawer.signup.success, and the window event
 * 'sofsy:lead' for assets/signup-conversions.js) BEFORE the caller shows the thank-you. That is the only place the
 * trimmed address goes besides Klaviyo, and only so sofsy-track.js can hash it; never on the bot path, a failed send or
 * a timeout. watchFooterForm also still watches the inline opacity of #ContactFooter-success (a fallback; its
 * once-per-page key is taken by then) and #ContactFooter-error, and the field's field__input--error class just after a
 * tap on the button or a submit. So:
 * - #ContactFooter-success gets style.opacity '1' ONLY after a 2xx from Klaviyo (email.signup.success). The
 *   pretend thank-you looks the same through CSS but never sets it, so a bot is never counted as a sign-up.
 * - #ContactFooter-error gets style.opacity '1' when the send fails (email.signup.fail, reason other).
 * - An empty or malformed address marks the field field__input--error (email.signup.fail, reason invalid_email).
 * Never the address itself.
 *
 * Shared with the welcome drawer (assets/welcome-drawer.js, zao#617): the same offer per visitor (assignOffer), the
 * same send, trap and 1 s gate (subscribe), and one "has signed up" mark (localStorage sofsy_signed_up = '1', set
 * after a real 2xx here or in the drawer, and on the submit event of sofsy's two Klaviyo sign-up pop-ups), all through
 * window.SofsyFooterSignup. The drawer sends signup_source 'drawer_menu' | 'drawer_auto', and signup_entry
 * 'drawer_menu' | 'drawer_auto' | 'drawer_exit' | 'drawer_campaign' (a sign-up campaign visit's automatic opening).
 * Every sign-up, footer and drawer, also sends profile properties for where she came from and the moment she signed
 * up (first_touch_*, last_touch_*, gclid / gbraid / wbraid, fbclid_present, fbc, signup_entry, signup_page_type,
 * signup_product_handle / _colour, signup_device, signup_pages_viewed, signup_bag_items): see signupProps below.
 */
(() => {
  'use strict';

  if (window.SofsyFooterSignup) return; // loaded twice: the first copy already listens

  const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions?company_id=VMQtQt';
  const REVISION = '2026-07-15';
  const LIST_ID = 'XdwVYY';
  const CUSTOM_SOURCE = 'Footer';
  const SIGNUP_SOURCE = 'footer';
  const TIMEOUT_MS = 15000;
  const MIN_FILL_MS = 1000;
  const ANNOUNCE_MS = 60;
  const OFFERS = ['gloves', 'pct10'];
  const OFFER_KEY = 'sofsy_signup_offer';
  const OFFER_FROM_KEY = 'sofsy_signup_offer_from'; // popup | popup_view | footer
  const SIGNED_UP_KEY = 'sofsy_signed_up'; // '1' once she has signed up here, in the drawer or in a Klaviyo form
  // Deliberately simple: one @, no spaces, a dot in the domain, at least two letters after it. Klaviyo checks again.
  const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/;
  // The same test submit() applies (it trims first). Exposed below so the unit test can run it; submit() itself is unchanged.
  const isEmail = (value) => EMAIL.test(String(value == null ? '' : value).trim());

  const now = () => (window.performance && typeof window.performance.now === 'function' ? window.performance.now() : Date.now());

  /* ---------- the offer ----------
   * Which offer the footer shows and sends, first match wins (what is shown is always what is sent):
   *   1. the offer she submitted in a Klaviyo pop-up: the klaviyoForms 'submit' event for the pop-ups below,
   *      metaData.signup_offer only (metaData also holds her email and phone, which are never read). Kept with
   *      OFFER_FROM_KEY 'popup', so it outranks everything on later visits too.
   *   2. the pop-up variant she was shown:
   *      a. the klaviyoForms 'open' / 'embedOpen' event's formVersionId (Klaviyo's onsite script sends it on these
   *         events, seen in its live code 4 Oct; the docs page lists type, formId, companyId and metaData only),
   *         mapped through POPUP_VERSION_OFFERS; kept with OFFER_FROM_KEY 'popup_view';
   *      b. fallback: Klaviyo's own localStorage 'klaviyoOnsite', which records the version shown per form id
   *         (seen live 4 Oct: {"viewedForms":{"modal":{"viewedForms":{"Xkv52p":28757552},...}}}). NOT documented, so
   *         every step is guarded and anything unexpected means "no answer".
   *   3. the footer's own stored pick (OFFER_KEY);
   *   4. a new 50/50 pick, stored (decided once per page load when storage is unavailable).
   * Pop-up events always update storage (for her next visit), but change the footer on screen only until she touches it.
   */
  const POPUP_FORMS = ['Xkv52p', 'Y9SG6D']; // Klaviyo pop-ups: desktop Xkv52p, mobile Y9SG6D
  // Pop-up A/B variant version id -> offer. EMPTY until Ben creates the pop-up A/B (kf1c): then add the 4 version ids
  // (2 per form, from GET /api/forms/{id}/form-versions or the Klaviyo form editor), e.g. '12345678': 'gloves'.
  // While it is empty, step 2 never answers and the footer keeps its own 50/50.
  const POPUP_VERSION_OFFERS = Object.freeze({
    // Xkv52p (desktop): '<version id>': 'gloves', '<version id>': 'pct10',
    // Y9SG6D (mobile):  '<version id>': 'gloves', '<version id>': 'pct10',
  });
  const isOffer = (value) => typeof value === 'string' && OFFERS.indexOf(value) > -1;
  const own = (obj, key) => !!obj && typeof obj === 'object' && Object.prototype.hasOwnProperty.call(obj, key);
  const versionOffer = (version, map) => {
    if (typeof version !== 'number' && typeof version !== 'string') return null;
    const key = String(version);
    return own(map, key) && isOffer(map[key]) ? map[key] : null;
  };

  // 2b, pure: the raw 'klaviyoOnsite' string -> 'gloves' | 'pct10' | null. Never throws.
  const offerFromViewed = (raw, map) => {
    try {
      if (typeof raw !== 'string' || !raw) return null;
      const data = JSON.parse(raw);
      const outer = own(data, 'viewedForms') ? data.viewedForms : null;
      const viewed = own(outer, 'modal') && own(outer.modal, 'viewedForms') ? outer.modal.viewedForms : null;
      if (!viewed || typeof viewed !== 'object' || Array.isArray(viewed)) return null;
      for (let i = 0; i < POPUP_FORMS.length; i += 1) {
        const offer = own(viewed, POPUP_FORMS[i]) ? versionOffer(viewed[POPUP_FORMS[i]], map) : null;
        if (offer) return offer;
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  // 1 and 2a, pure: a klaviyoForms event detail -> { offer, from } | null. Reads type, formId, formVersionId and
  // metaData.signup_offer only. Never throws.
  const offerFromFormEvent = (detail, map) => {
    try {
      if (!detail || typeof detail !== 'object' || POPUP_FORMS.indexOf(detail.formId) < 0) return null;
      if (detail.type === 'submit') {
        const meta = detail.metaData;
        const value = own(meta, 'signup_offer') ? meta.signup_offer : null;
        if (isOffer(value)) return { offer: value, from: 'popup' };
      }
      if (detail.type === 'open' || detail.type === 'embedOpen' || detail.type === 'submit') {
        const viewed = versionOffer(detail.formVersionId, map);
        if (viewed) return { offer: viewed, from: 'popup_view' };
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  const safeGet = (storage, key) => {
    try {
      return storage ? storage.getItem(key) : null;
    } catch (_) {
      return null;
    }
  };
  const safeSet = (storage, key, value) => {
    try {
      if (storage) storage.setItem(key, value);
      return !!storage;
    } catch (_) {
      return false;
    }
  };
  const pageStorage = () => {
    try {
      return window.localStorage;
    } catch (_) {
      return null;
    }
  };

  let pageOffer = null; // the 50/50 for this page load when storage cannot keep it
  const pageEvent = { popup: null, view: null }; // answers from klaviyoForms events on this page (work without storage)
  const randomOffer = () => OFFERS[Math.random() < 0.5 ? 0 : 1];

  // Steps 1-4 against a storage object (window.localStorage, or a test double). Never throws.
  const resolveOffer = (storage, map) => {
    if (pageEvent.popup) return pageEvent.popup;
    const kept = safeGet(storage, OFFER_KEY);
    const from = safeGet(storage, OFFER_FROM_KEY);
    if (isOffer(kept) && from === 'popup') return kept;
    if (pageEvent.view) return pageEvent.view;
    if (isOffer(kept) && from === 'popup_view') return kept;
    const viewed = offerFromViewed(safeGet(storage, 'klaviyoOnsite'), map);
    if (viewed) {
      if (safeSet(storage, OFFER_KEY, viewed)) safeSet(storage, OFFER_FROM_KEY, 'popup_view');
      return viewed;
    }
    if (isOffer(kept)) return kept;
    if (!pageOffer) pageOffer = randomOffer();
    if (safeSet(storage, OFFER_KEY, pageOffer)) safeSet(storage, OFFER_FROM_KEY, 'footer');
    return pageOffer;
  };
  const assignOffer = () => resolveOffer(pageStorage(), POPUP_VERSION_OFFERS);

  // The "has signed up" mark the welcome drawer reads (it never shows to someone who has). Storage only: no address.
  let signedUpThisPage = false; // when storage cannot keep the mark, this page view still knows
  const markSignedUp = () => {
    signedUpThisPage = true;
    safeSet(pageStorage(), SIGNED_UP_KEY, '1');
  };
  const isSignedUp = () => signedUpThisPage || safeGet(pageStorage(), SIGNED_UP_KEY) === '1';

  /* ---------- where she came from, and the moment she signed up (zao#617, Ben 5 Oct) ----------
   * Sent as Klaviyo profile properties with every sign-up from the footer and the welcome drawer, for later reporting,
   * offline conversions (gclid / gbraid / wbraid) and Meta CAPI matching (_fbc, fbclid). Short, closed values only;
   * never anything she typed except the email itself, never a query string, never a value that looks like an address.
   *   first touch: the first page view on this device that this script saw after it shipped (localStorage, written
   *                once, never replaced);
   *   last touch:  the latest page load whose address carried a utm_* or a click id (replaced each time);
   *   each touch:  utm_source, utm_medium, utm_campaign, utm_content, utm_term, landing_path (cleaned, see
   *                cleanLandingPath), referrer_host (another site's host name only), date (ISO);
   *   click ids:   the latest gclid, gbraid, wbraid (raw, validated, never cut), and whether an fbclid was seen;
   *   pages:       this visit's page count (sessionStorage).
   * The click ids, fbclid_present and fbc are SENT only with advertising consent (adConsent, assets/cart.js's F5
   * rule); without it they are left out of the sign-up.
   * captureTouch runs once per page load, here, because this file is on every page (the footer, and the drawer's
   * snippet). assets/attribution-capture.js keeps its own sofsy_attr cookie for the order; this does not touch it. */
  const FIRST_TOUCH_KEY = 'sofsy_first_touch';
  const LAST_TOUCH_KEY = 'sofsy_last_touch';
  const CLICK_IDS_KEY = 'sofsy_click_ids';
  const PAGES_KEY = 'sofsy_pages_viewed'; // sessionStorage
  const TOUCH_UTMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const TOUCH_FIELDS = TOUCH_UTMS.concat(['landing_path', 'referrer_host', 'date']);
  const TAGS = TOUCH_UTMS.concat(['utm_id', 'gclid', 'gbraid', 'wbraid', 'dclid', 'fbclid', 'msclkid', 'ttclid', 'srsltid']);
  const GOOGLE_CLICKS = ['gclid', 'gbraid', 'wbraid'];
  const PROP_MAX = 100; // characters, every text property
  const ID_MAX = 500; // a click id or _fbc longer than this is dropped, never cut (a cut id matches nothing)
  // Formats, mirrored from assets/cart.js (sofsyFbc, sofsyFbclid): Meta's _fbc, and Google's / Meta's id alphabet.
  const FBC = /^fb\.\d\.\d{10,16}\.[A-Za-z0-9_-]{1,500}$/;
  const CLICK_ID = /^[A-Za-z0-9_-]{1,500}$/;
  const ENTRIES = ['footer', 'drawer_menu', 'drawer_auto', 'drawer_exit', 'drawer_campaign'];
  // Account and checkout paths carry tokens: only their first segment is kept (after an optional market prefix).
  const PRIVATE_ROOTS = ['account', 'customers', 'orders', 'checkouts', 'cart', 'discount'];
  // An email address written out to dodge filters: "name (at) site", "name [at] site".
  const SPELLED_AT = /[[(]\s*at\s*[\])]/i;

  const decode = (value) => {
    try {
      return decodeURIComponent(String(value).replace(/\+/g, ' '));
    } catch (_) {
      return String(value);
    }
  };

  // One short text value: decoded first (so %40 is seen as @), cut at any ? or # (no query string or fragment ever
  // goes on), whitespace folded; empty when it looks like an email address (an @, or a spelled-out (at) / [at]), and,
  // for a utm (utm true), when it holds a run of 7 or more digits (phone-like); at most max characters.
  const short = (value, max, utm) => {
    if (value === null || value === undefined) return '';
    if (typeof value !== 'string' && typeof value !== 'number') return '';
    const v = decode(value).split(/[?#]/)[0].replace(/\s+/g, ' ').trim();
    if (!v || v.indexOf('@') > -1 || SPELLED_AT.test(v)) return '';
    if (utm && /\d{7,}/.test(v)) return '';
    return v.slice(0, max || PROP_MAX);
  };

  // A click id or _fbc as it came, or '' when it is too long or not in its format. Never cut.
  const validId = (value, format) => {
    if (typeof value !== 'string') return '';
    const v = value.trim();
    return v && v.length <= ID_MAX && format.test(v) ? v : '';
  };

  // The landing path, safe to keep: segments decoded; an account / checkout path cut to its first segment; any segment
  // that looks like a token (16+ hex, 16+ characters with a digit or a capital, mostly digits from 6) becomes :token,
  // anything else outside a plain slug alphabet :x. Mirrors cleanPath in zao benmay-io/workers/chopsticks/pixel.js.
  const cleanLandingPath = (value) => {
    if (typeof value !== 'string' || value.charAt(0) !== '/') return '/';
    const raw = value.split(/[?#]/)[0].split('/').slice(1, 11);
    const segs = raw.map((r) => {
      try {
        return decodeURIComponent(r);
      } catch (_) {
        return ':x';
      }
    });
    const market = segs.length > 1 && /^[a-z]{2}(-[a-z]{2})?$/i.test(segs[0]) ? 1 : 0;
    const root = (segs[market] || '').toLowerCase();
    if (PRIVATE_ROOTS.indexOf(root) > -1) return `/${segs.slice(0, market).concat(root).join('/')}`;
    const out = segs.map((seg) => {
      if (seg === '') return '';
      const digits = (seg.match(/\d/g) || []).length;
      if (/^[a-f0-9]{16,}$/i.test(seg)) return ':token';
      if (seg.length >= 16 && (/\d/.test(seg) || /[A-Z]/.test(seg))) return ':token';
      if (digits >= 6 && digits * 2 > seg.length) return ':token';
      if (!/^[\w.~-]{0,64}$/.test(seg)) return ':x';
      return seg;
    });
    return `/${out.join('/')}`.slice(0, PROP_MAX);
  };

  // Advertising consent, assets/cart.js's F5 rule (metaIdsNow): Shopify's Customer Privacy API present and BOTH
  // marketingAllowed() and saleOfDataAllowed() true; plus no Global Privacy Control signal from the browser.
  const adConsent = () => {
    try {
      const nav = window.navigator;
      if (nav && nav.globalPrivacyControl === true) return false;
      const api = window.Shopify && window.Shopify.customerPrivacy;
      return !!(
        api &&
        typeof api.marketingAllowed === 'function' &&
        typeof api.saleOfDataAllowed === 'function' &&
        api.marketingAllowed() === true &&
        api.saleOfDataAllowed() === true
      );
    } catch (_) {
      return false;
    }
  };

  const parseJson = (raw) => {
    try {
      const v = raw ? JSON.parse(raw) : null;
      return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
    } catch (_) {
      return null;
    }
  };
  const sessionStore = () => {
    try {
      return window.sessionStorage;
    } catch (_) {
      return null;
    }
  };

  // One touch from an address (location-like: pathname, search, hostname) and a referrer URL. Pure.
  const touchFrom = (loc, referrer, nowIso) => {
    const params = new URLSearchParams((loc && loc.search) || '');
    const t = {};
    TOUCH_UTMS.forEach((k) => {
      const v = short(params.get(k), PROP_MAX, true);
      if (v) t[k] = v;
    });
    t.landing_path = cleanLandingPath((loc && loc.pathname) || '/');
    try {
      if (referrer) {
        const host = new URL(referrer).hostname;
        if (host && host !== (loc && loc.hostname)) t.referrer_host = short(host);
      }
    } catch (_) {
      /* no referrer host */
    }
    t.date = short(nowIso, 40);
    return t;
  };
  const isTagged = (search) => {
    const params = new URLSearchParams(search || '');
    return TAGS.some((k) => {
      const v = params.get(k);
      return v !== null && String(v).trim() !== '';
    });
  };

  // Once per page load. storage: localStorage; session: sessionStorage (test doubles welcome). Never throws.
  const captureTouch = (storage, session, loc, referrer, nowIso) => {
    try {
      if (!parseJson(safeGet(storage, FIRST_TOUCH_KEY))) safeSet(storage, FIRST_TOUCH_KEY, JSON.stringify(touchFrom(loc, referrer, nowIso)));
      if (isTagged(loc && loc.search)) safeSet(storage, LAST_TOUCH_KEY, JSON.stringify(touchFrom(loc, referrer, nowIso)));
      const params = new URLSearchParams((loc && loc.search) || '');
      const clicks = parseJson(safeGet(storage, CLICK_IDS_KEY)) || {};
      let changed = false;
      GOOGLE_CLICKS.forEach((k) => {
        const v = validId(params.get(k), CLICK_ID);
        if (v) {
          clicks[k] = v;
          changed = true;
        }
      });
      if (validId(params.get('fbclid'), CLICK_ID)) {
        clicks.fbclid_present = 'yes';
        changed = true;
      }
      if (changed) safeSet(storage, CLICK_IDS_KEY, JSON.stringify(clicks));
      const pages = Number(safeGet(session, PAGES_KEY)) || 0;
      safeSet(session, PAGES_KEY, String(pages + 1));
    } catch (_) {
      /* attribution is a courtesy; the sign-up never depends on it */
    }
  };

  const readCookie = (name) => {
    try {
      const m = new RegExp('(?:^|;\\s*)' + name + '=([^;]*)').exec(String(document.cookie || ''));
      return m ? decodeURIComponent(m[1]) : '';
    } catch (_) {
      return '';
    }
  };
  const pageContext = () => {
    try {
      const el = document.getElementById('clarity-ctx');
      return (el && parseJson(el.textContent)) || {};
    } catch (_) {
      return {};
    }
  };
  const bagItems = () => {
    try {
      const el = document.getElementById('bag-state');
      const n = el ? Number(JSON.parse(el.textContent).item_count) : NaN;
      return Number.isSafeInteger(n) && n >= 0 ? n : null;
    } catch (_) {
      return null;
    }
  };
  const deviceOf = (width) => (width < 750 ? 'phone' : width < 1025 ? 'tablet' : 'desktop');

  // The profile properties for one sign-up (besides signup_offer and signup_source). env, for tests: { storage,
  // session, cookie, pageType, path, colour, width, bag }; otherwise read from the page. Empty values are left out, so a
  // sign-up never blanks a property Klaviyo already holds.
  const signupProps = (entry, env) => {
    const e = env || {};
    const storage = 'storage' in e ? e.storage : pageStorage();
    const session = 'session' in e ? e.session : sessionStore();
    const out = {};
    const put = (key, value, max, utm) => {
      const v = short(value, max, utm);
      if (v) out[key] = v;
    };
    const touches = [['first_touch_', FIRST_TOUCH_KEY], ['last_touch_', LAST_TOUCH_KEY]];
    touches.forEach(([prefix, key]) => {
      const t = parseJson(safeGet(storage, key)) || {};
      TOUCH_FIELDS.forEach((f) => {
        if (f === 'landing_path') {
          if (typeof t[f] === 'string') out[prefix + f] = cleanLandingPath(t[f]); // stored values are re-cleaned
        } else put(prefix + f, t[f], f === 'date' ? 40 : PROP_MAX, TOUCH_UTMS.indexOf(f) > -1);
      });
    });
    // Advertising ids only with advertising consent (F5, see adConsent); otherwise none of them is sent.
    if ('consent' in e ? e.consent === true : adConsent()) {
      const clicks = parseJson(safeGet(storage, CLICK_IDS_KEY)) || {};
      GOOGLE_CLICKS.forEach((k) => {
        const v = validId(clicks[k], CLICK_ID);
        if (v) out[k] = v;
      });
      out.fbclid_present = clicks.fbclid_present === 'yes' ? 'yes' : 'no';
      const fbc = validId('cookie' in e ? e.cookie : readCookie('_fbc'), FBC);
      if (fbc) out.fbc = fbc;
    }
    if (ENTRIES.indexOf(entry) > -1) out.signup_entry = entry;
    const ctx = 'pageType' in e ? { pageType: e.pageType } : pageContext();
    put('signup_page_type', ctx.pageType, 40);
    if (ctx.pageType === 'product') {
      const path = 'path' in e ? e.path : (window.location && window.location.pathname) || '';
      const m = /\/products\/([^/?#]+)/.exec(path || '');
      let handle = '';
      try {
        handle = m ? decodeURIComponent(m[1]) : '';
      } catch (_) {
        handle = '';
      }
      put('signup_product_handle', handle);
      let colour = 'colour' in e ? e.colour : '';
      if (!('colour' in e)) {
        try {
          const cs = document.querySelector('[data-pdp-cs]');
          colour = cs ? cs.getAttribute('data-shade') : '';
        } catch (_) {
          colour = '';
        }
      }
      put('signup_product_colour', colour);
    }
    const width = 'width' in e ? e.width : window.innerWidth || 0;
    if (width > 0) out.signup_device = deviceOf(width);
    const pages = Number(safeGet(session, PAGES_KEY));
    if (Number.isSafeInteger(pages) && pages > 0) out.signup_pages_viewed = pages;
    const bag = 'bag' in e ? e.bag : bagItems();
    if (Number.isSafeInteger(bag) && bag >= 0) out.signup_bag_items = bag;
    return out;
  };

  /* ---------- states ---------- */

  const parts = (form) => ({
    input: form.querySelector('input[name="email"]'),
    trap: form.querySelector('input[name="sofsy_hp_x"]'),
    button: form.querySelector('button[type="submit"]'),
    fieldError: form.querySelector('#ContactFooter-field-error'),
    sendError: form.querySelector('#ContactFooter-error'),
    success: form.querySelector('#ContactFooter-success'),
  });

  // Live-region words: cleared first, then set a moment later, so the same message said twice is announced twice.
  const timers = new WeakMap();
  const say = (el, text, after) => {
    if (!el) return;
    window.clearTimeout(timers.get(el));
    el.textContent = '';
    if (!text) return;
    timers.set(el, window.setTimeout(() => {
      el.textContent = text;
      if (after) after();
    }, ANNOUNCE_MS));
  };

  const clearFieldError = (form) => {
    const p = parts(form);
    if (p.input) {
      p.input.classList.remove('field__input--error');
      p.input.removeAttribute('aria-invalid');
      p.input.removeAttribute('aria-describedby');
    }
    say(p.fieldError, '');
  };

  const clearSendError = (form) => {
    const p = parts(form);
    if (!p.sendError) return;
    say(p.sendError, '');
    p.sendError.style.opacity = '';
  };

  const showFieldError = (form, text) => {
    const p = parts(form);
    clearSendError(form);
    if (p.input) {
      // The class is set at once: sofsy-track.js reads it one tick after the tap (email.signup.fail invalid_email).
      p.input.classList.add('field__input--error');
      p.input.setAttribute('aria-invalid', 'true');
      p.input.setAttribute('aria-describedby', 'ContactFooter-field-error');
      p.input.focus({ preventScroll: true });
    }
    say(p.fieldError, text);
  };

  const showSendError = (form) => {
    const p = parts(form);
    if (!p.sendError) return;
    say(p.sendError, form.dataset.msgNetwork || '');
    p.sendError.style.opacity = '1'; // sofsy-track.js: email.signup.fail {reason: other}
  };

  const markStarted = (form) => {
    if (!form.dataset.signupStartedAt) form.dataset.signupStartedAt = String(now() || 1);
  };
  const primeRestored = (form) => {
    const input = form.querySelector('input[name="email"]');
    if (input && input.value.trim() !== '') markStarted(form);
  };

  const setBusy = (form, busy) => {
    const p = parts(form);
    if (busy) {
      form.dataset.signupBusy = 'true';
      form.setAttribute('aria-busy', 'true');
      if (p.button) p.button.setAttribute('aria-disabled', 'true');
    } else {
      delete form.dataset.signupBusy;
      form.removeAttribute('aria-busy');
      if (p.button) p.button.removeAttribute('aria-disabled');
    }
  };

  // real: Klaviyo answered 2xx. A pretend thank-you (a bot) looks the same but is never reported as a sign-up.
  // The thank-you is not a live region: focus moves to it (the field and button it replaces are gone), and that is
  // the one announcement.
  const showSuccess = (form, real) => {
    const p = parts(form);
    form.dataset.signupDone = 'true';
    form.classList.add('footer-signup--done');
    if (!p.success) return;
    p.success.textContent = form.dataset.msgSuccess || '';
    if (real) p.success.style.opacity = '1'; // sofsy-track.js: email.signup.success
    p.success.focus({ preventScroll: true });
  };

  // After a real 2xx only: tell Klaviyo's own onsite script who she is, so its pop-ups (which hide from identified
  // people) stop asking her. The address goes only to Klaviyo's script, never to tracking. Never throws.
  const identifyToKlaviyo = (email) => {
    try {
      const k = window.klaviyo;
      if (!k) return;
      let result = null;
      if (typeof k.identify === 'function') result = k.identify({ email });
      else if (typeof k.push === 'function') k.push(['identify', { email }]);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (_) {
      /* identifying is a courtesy; the sign-up already succeeded */
    }
  };

  /* ---------- send ---------- */

  // extra (the welcome drawer only): { customSource, signupSource, signupEntry, offerForced }. Without it, the footer's
  // own values. offerForced true (the drawer's FORCED OFFER, wd_offer from an ad) adds signup_offer_forced 'yes'.
  // Every sign-up also carries signupProps (where she came from, the page, the device, the bag), built at send time.
  const send = (email, offer, extra) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), TIMEOUT_MS) : null;
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: (extra && extra.customSource) || CUSTOM_SOURCE,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                properties: Object.assign(
                  signupProps((extra && extra.signupEntry) || 'footer'),
                  { signup_offer: offer, signup_source: (extra && extra.signupSource) || SIGNUP_SOURCE },
                  extra && extra.offerForced === true ? { signup_offer_forced: 'yes' } : {}
                ),
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: LIST_ID } } },
      },
    };
    return window.fetch(ENDPOINT, {
      method: 'POST',
      headers: { accept: 'application/vnd.api+json', revision: REVISION, 'content-type': 'application/vnd.api+json' },
      body: JSON.stringify(payload),
      signal: controller ? controller.signal : undefined,
    }).finally(() => window.clearTimeout(timer));
  };

  // After a real 2xx only: the sign-up goes to sofsy-track.js's one lead decider (SofsyTrack.signupSuccess), with the
  // trimmed address so it can hash it (it keeps nothing). o.signup: {surface: 'footer' | 'drawer', entry?}; without it,
  // nothing is reported here. Never throws.
  const reportSignup = (o) => {
    try {
      const t = window.SofsyTrack;
      if (!o.signup || !t || typeof t.signupSuccess !== 'function') return;
      t.signupSuccess({ surface: o.signup.surface, entry: o.signup.entry, offer: o.offer, email: String(o.email || '').trim() });
    } catch (_) {
      /* tracking never breaks the sign-up */
    }
  };

  // The send every sign-up form here shares: the trap, the 1 s gate, one request, then identify and the mark.
  // Resolves 'bot' (nothing sent: a filled trap or no touch at all), 'success' (a 2xx), 'api' or 'network'.
  // startedAt: performance.now() at her first touch of the form. A send under 1 s from it waits out the rest.
  // signup: {surface, entry?}, for the lead decider (reportSignup), before the caller shows the thank-you.
  const subscribe = (opts) => {
    const o = opts || {};
    const startedAt = Number(o.startedAt);
    if (o.trap || !startedAt) return Promise.resolve('bot');
    const go = () => send(o.email, o.offer, o.extra)
      .then((response) => (response.ok ? 'success' : 'api'))
      .catch(() => 'network')
      .then((kind) => {
        if (kind === 'success') {
          markSignedUp();
          identifyToKlaviyo(o.email);
          reportSignup(o);
        }
        return kind;
      });
    const left = MIN_FILL_MS - (now() - startedAt);
    return left > 0 ? new Promise((resolve) => window.setTimeout(() => resolve(go()), left)) : go();
  };

  const submit = (form) => {
    if (form.dataset.signupBusy === 'true' || form.dataset.signupDone === 'true') return;
    const p = parts(form);
    const email = p.input ? p.input.value.trim() : '';

    // Her mistakes first, so a person who is quick still sees why nothing happened.
    if (!email) return showFieldError(form, form.dataset.msgEmpty || '');
    if (!EMAIL.test(email)) return showFieldError(form, form.dataset.msgInvalid || '');
    clearFieldError(form);
    clearSendError(form);

    // Bots: a filled trap field, or no touch of the form at all.
    const startedAt = Number(form.dataset.signupStartedAt);
    if ((p.trap && p.trap.value) || !startedAt) return showSuccess(form, false);

    const offer = form.dataset.offer;
    setBusy(form, true); // busy from the tap: a second tap while she waits is ignored
    // Under 1 s from her first touch: subscribe waits out the rest, then sends. Never dropped for speed.
    subscribe({ email, offer, startedAt, signup: { surface: 'footer' } }).then((kind) => {
      setBusy(form, false);
      if (kind === 'success') showSuccess(form, true);
      else showSendError(form);
    });
  };

  /* ---------- wiring ---------- */

  const init = (form) => {
    if (!form || form.dataset.signupReady === 'true') return;
    form.dataset.signupReady = 'true';
    form.dataset.offer = assignOffer();

    // The 1 s clock starts at her first touch (focus anywhere in the form, or typing, autofill included). From that
    // touch on, the offer is locked for this page view: pop-up events and storage no longer change what she sees.
    const started = () => markStarted(form);
    form.addEventListener('focusin', started);
    form.addEventListener('input', started);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submit(form);
    });

    const input = form.querySelector('input[name="email"]');
    if (input) {
      // The label floats above a filled field (style.css .field__input--filled), as it did before.
      const filled = () => input.classList.toggle('field__input--filled', input.value !== '');
      input.addEventListener('input', () => {
        filled();
        if (input.classList.contains('field__input--error')) clearFieldError(form);
      });
      input.addEventListener('change', filled);
      filled();
    }
    primeRestored(form);
    form.classList.add('footer-signup--ready'); // only now is the form shown (sofsy-ds.css)
  };

  const initAll = () => document.querySelectorAll('form[data-footer-signup]').forEach(init);

  // A field that already holds an address when the form is set up (the browser restored it: back/forward cache, a
  // Firefox reload, form restore) counts as her first touch, so a later tap is never taken for a bot. Same again when
  // the page comes back from the back/forward cache (pageshow persisted).
  window.addEventListener('pageshow', (event) => {
    if (!event || !event.persisted) return;
    document.querySelectorAll('form[data-footer-signup]').forEach((form) => {
      if (form.dataset.signupReady !== 'true') return;
      const input = form.querySelector('input[name="email"]');
      if (input) input.classList.toggle('field__input--filled', input.value !== '');
      primeRestored(form);
    });
  });

  // Show the current answer in every footer form she has not touched yet (once touched, sending or sent, the offer is
  // locked for the page view). The send always uses the form's data-offer, so what is shown is what is sent.
  const refreshOffers = () => {
    const offer = assignOffer();
    document.querySelectorAll('form[data-footer-signup]').forEach((form) => {
      if (form.dataset.signupReady !== 'true' || form.dataset.signupStartedAt || form.dataset.signupBusy === 'true' || form.dataset.signupDone === 'true') return;
      form.dataset.offer = offer;
    });
  };

  // Klaviyo's klaviyoForms window event (developers.klaviyo.com, "Track Klaviyo form activity using JavaScript").
  // sofsy-track.js listens to the same event for its own reports; this listener only decides the footer's offer.
  window.addEventListener('klaviyoForms', (event) => {
    try {
      const detail = event && event.detail;
      // She signed up in one of sofsy's two Klaviyo sign-up pop-ups (POPUP_FORMS): the welcome drawer stops showing.
      // Klaviyo's 'submit' type is its successful-submission event (a step or a failed send is not 'submit'); any
      // other form id, back-in-stock included, never marks her. Reads type and formId only, never metaData.
      if (detail && typeof detail === 'object' && detail.type === 'submit' && POPUP_FORMS.indexOf(detail.formId) > -1) markSignedUp();
      const answer = offerFromFormEvent(detail, POPUP_VERSION_OFFERS);
      const storage = pageStorage();
      if (answer && answer.from === 'popup') {
        pageEvent.popup = answer.offer;
        if (safeSet(storage, OFFER_KEY, answer.offer)) safeSet(storage, OFFER_FROM_KEY, 'popup');
        refreshOffers();
      } else if (answer && !pageEvent.popup && safeGet(storage, OFFER_FROM_KEY) !== 'popup') {
        pageEvent.view = answer.offer;
        if (safeSet(storage, OFFER_KEY, answer.offer)) safeSet(storage, OFFER_FROM_KEY, 'popup_view');
        refreshOffers();
      } else if (!answer && detail && typeof detail === 'object' && (detail.type === 'open' || detail.type === 'embedOpen') && POPUP_FORMS.indexOf(detail.formId) > -1) {
        // No version id we know: Klaviyo writes klaviyoOnsite as it opens; check that fallback once it has.
        window.setTimeout(refreshOffers, 0);
      }
    } catch (_) {
      /* a bad event never touches the footer */
    }
  });

  // Pure helpers for tests, and the shared sign-up pieces the welcome drawer uses (assets/welcome-drawer.js).
  window.SofsyFooterSignup = Object.freeze({
    offerFromViewed,
    offerFromFormEvent,
    resolveOffer,
    isEmail,
    POPUP_FORMS,
    POPUP_VERSION_OFFERS,
    captureTouch,
    touchFrom,
    signupProps,
    FIRST_TOUCH_KEY,
    LAST_TOUCH_KEY,
    CLICK_IDS_KEY,
    PAGES_KEY,
    PROP_MAX,
    ID_MAX,
    cleanLandingPath,
    assignOffer,
    subscribe,
    markSignedUp,
    isSignedUp,
    MIN_FILL_MS,
    SIGNED_UP_KEY,
  });

  // Once per page load (this file runs once: see the guard at the top).
  try {
    captureTouch(pageStorage(), sessionStore(), window.location, document.referrer, new Date().toISOString());
  } catch (_) {
    /* never blocks the form */
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  // The theme editor re-renders the footer section.
  document.addEventListener('shopify:section:load', initAll);
})();
